import React, { useState } from 'react';
import { WheelSafeNumberInput } from '../common/WheelSafeNumberInput';
import {
    Edit,
    SimpleForm,
    TextInput,
    SelectInput,
    BooleanInput,
    required,
    minValue,
    maxValue,
    maxLength,
    useTranslate,
    useNotify,
    useRedirect,
    useRecordContext,
    Toolbar,
    SaveButton,
    useGetList,
    useDataProvider,
    FormDataConsumer,
} from 'react-admin';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Alert,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField as MuiTextField,
    Tab,
    Tabs,
    Chip,
    Divider,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import {
    ArrowBack,
    Info,
    Warning,
    Delete,
    Assessment,
    Speed,
    Timer,
    TrendingUp,
    Power,
} from '@mui/icons-material';
import { ServerErrorBinder } from '../common/ServerErrorBinder';
import { translateServerMessage } from '../../utils/translateServerMessage';

// Backend uses *Minutes for these thresholds; form uses *Seconds.
// Map server error paths back to the form source so the right input is marked.
const OCPP_TEMPLATE_PATH_ALIAS: Record<string, string> = {
    approachingThresholdMinutes: 'approachingThresholdSeconds',
    nearThresholdMinutes: 'nearThresholdSeconds',
};

const mapOCPPTemplateBackendPath = (path: string): string => {
    const stripped = path
        .replace(/^body\./, '')
        .replace(/^params\./, '')
        .replace(/^query\./, '');
    return OCPP_TEMPLATE_PATH_ALIAS[stripped] || stripped;
};

const OCPP_TEMPLATE_KNOWN_FIELDS = new Set([
    'templateName', 'description', 'ocppVersion', 'ocppProtocol',
    'normalIntervalSeconds', 'initialFastIntervalEnabled',
    'initialFastIntervalSeconds', 'initialFastIntervalCount',
    'approachingIntervalEnabled', 'approachingIntervalSeconds',
    'approachingThresholdSeconds', 'nearIntervalEnabled',
    'nearIntervalSeconds', 'nearThresholdSeconds',
    'hysteresisEnabled', 'hysteresisMinutes',
    'powerSmoothingEnabled', 'powerSmoothingFactor', 'isActive',
]);

const ocppTemplateFieldLabelKey = (field: string): string =>
    OCPP_TEMPLATE_KNOWN_FIELDS.has(field)
        ? `resources.ocpp-templates.fields.${field}`
        : field;

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

const TabPanel = (props: TabPanelProps) => {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            style={{ maxWidth: '100%' }}
            {...other}
        >
            {value === index && (
                <Box sx={{ pt: { xs: 2, sm: 3 }, maxWidth: '100%' }}>
                    {children}
                </Box>
            )}
        </div>
    );
};

const DeleteTemplateSection = () => {
    const record = useRecordContext();
    const [open, setOpen] = useState(false);
    const [confirmText, setConfirmText] = useState('');
    const notify = useNotify();
    const redirect = useRedirect();
    const dataProvider = useDataProvider();
    const translate = useTranslate();
    
    // Check if template is in use
    const { data: chargers = [] } = useGetList('chargers', {
        filter: { ocppTemplateId: record?.id },
        pagination: { page: 1, perPage: 1 },
    });
    
    const isInUse = chargers.length > 0;
    const isDefault = record?.isDefault;
    
    const handleClick = () => {
        setOpen(true);
        setConfirmText('');
    };
    
    const handleClose = () => {
        setOpen(false);
        setConfirmText('');
    };
    
    const handleConfirm = async () => {
        if (confirmText === record?.templateName) {
            try {
                await dataProvider.delete('ocpp-templates', { id: record.id });
                notify(translate('resources.ocpp-templates.notifications.deleted'), { type: 'success' });
                redirect('/ocpp-templates');
            } catch (error) {
                notify(translate('resources.ocpp-templates.notifications.deleteError'), { type: 'error' });
            }
            setOpen(false);
        }
    };
    
    const getDisabledReason = () => {
        if (isDefault) return translate('resources.ocpp-templates.delete.isDefault');
        if (isInUse) return translate('resources.ocpp-templates.delete.hasChargersContent');
        return null;
    };
    
    const canDelete = !isDefault && !isInUse;
    
    return (
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: { xs: 2, sm: 0 } }}>
            <Box>
                <Typography variant="body2" color="text.secondary">
                    {getDisabledReason() || translate('resources.ocpp-templates.delete.content')}
                </Typography>
            </Box>
            <Button
                variant="outlined"
                onClick={handleClick}
                disabled={!canDelete}
                sx={{ 
                    borderColor: canDelete ? '#F44336' : '#BDBDBD',
                    color: canDelete ? '#F44336' : '#BDBDBD',
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 3,
                    py: 1.5,
                    '&:hover': {
                        borderColor: canDelete ? '#D32F2F' : '#BDBDBD',
                        backgroundColor: canDelete ? 'rgba(244,67,54,0.08)' : 'transparent',
                    },
                    '&.Mui-disabled': {
                        borderColor: '#BDBDBD',
                        color: '#BDBDBD',
                    },
                }}
                title={getDisabledReason() || translate('common.delete')}
            >
                {translate('common.delete')}
            </Button>
            
            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: 3 }
                }}
            >
                <DialogTitle sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1,
                    pb: 1,
                }}>
                    <Delete sx={{ color: '#F44336' }} />
                    <Typography variant="h6">
                        {translate('resources.ocpp-templates.delete.title')}
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ mb: 3 }}>
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {translate('resources.ocpp-templates.delete.warning')}
                        </Alert>
                        <Typography variant="body1" sx={{ mb: 2 }}>
                            {translate('resources.ocpp-templates.delete.confirmMessage', { name: record?.templateName })}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {translate('resources.ocpp-templates.delete.typeToConfirm', { name: record?.templateName })}
                        </Typography>
                        <MuiTextField
                            autoFocus
                            fullWidth
                            value={confirmText}
                            onChange={(e) => setConfirmText(e.target.value)}
                            placeholder={record?.templateName}
                            error={confirmText !== '' && confirmText !== record?.templateName}
                            helperText={confirmText !== '' && confirmText !== record?.templateName ? translate('resources.ocpp-templates.delete.nameDoesNotMatch') : ''}
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3 }}>
                    <Button 
                        onClick={handleClose}
                        sx={{ 
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 600,
                            px: 3,
                        }}
                    >
                        {translate('ra.action.cancel')}
                    </Button>
                    <Button 
                        onClick={handleConfirm}
                        disabled={confirmText !== record?.templateName}
                        variant="contained"
                        sx={{ 
                            backgroundColor: '#F44336',
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 600,
                            px: 3,
                            '&:hover': {
                                backgroundColor: '#D32F2F',
                            },
                            '&.Mui-disabled': {
                                backgroundColor: '#BDBDBD',
                            },
                        }}
                    >
                        {translate('resources.ocpp-templates.delete.button')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

const EditToolbar = () => {
    return (
        <Toolbar
            sx={{
                backgroundColor: '#FAFAFA',
                borderTop: '1px solid #E0E0E0',
                px: { xs: 2, sm: 3 },
                py: 2,
                mt: { xs: 2, sm: 4 },
                '& .MuiButton-root': {
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 3,
                    py: 1.5,
                },
            }}
        >
            <SaveButton 
                variant="contained"
                sx={{
                    backgroundColor: '#009A75',
                    '&:hover': {
                        backgroundColor: '#007F5F',
                        boxShadow: '0 4px 12px rgba(0,154,117,0.3)',
                    },
                }}
            />
        </Toolbar>
    );
};

const PageHeader = () => {
    const record = useRecordContext();
    const redirect = useRedirect();
    const translate = useTranslate();
    
    return (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 1.5, sm: 2 },
            mb: { xs: 2, sm: 4 },
        }}>
            <IconButton
                onClick={() => redirect('/ocpp-templates')}
                size="large"
                sx={{
                    backgroundColor: '#FFFFFF',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    '&:hover': {
                        backgroundColor: '#F5F5F5',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                    },
                }}
                title={translate('resources.ocpp-templates.edit.backToList')}
            >
                <ArrowBack />
            </IconButton>
            <Box>
                <Typography
                    sx={{
                        fontWeight: 600,
                        mb: 0.5,
                        fontSize: { xs: '1.25rem', sm: '2.125rem' },
                        wordBreak: 'break-word',
                        overflowWrap: 'anywhere',
                    }}
                >
                    {translate('resources.ocpp-templates.edit.title', { name: record?.templateName })}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '1rem' } }}>
                    {translate('resources.ocpp-templates.edit.subtitle')}
                </Typography>
            </Box>
        </Box>
    );
};

const UsageOverviewSection = () => {
    const record = useRecordContext();
    const translate = useTranslate();
    
    const { data: chargers = [], isLoading } = useGetList('chargers', {
        filter: { ocppTemplateId: record?.id },
        pagination: { page: 1, perPage: 100 },
    });
    
    const totalPowerManaged = chargers.reduce((sum, charger) => sum + (charger.maxPower || 0), 0);
    
    if (!record) return null;
    
    return (
        <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
            <CardContent sx={{ p: { xs: 1.5, sm: 3 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, sm: 3 } }}>
                    <Assessment sx={{ mr: 2, color: '#009A75' }} />
                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                        {translate('resources.ocpp-templates.sections.usage')}
                    </Typography>
                </Box>

                <Box sx={{ display: 'grid', gap: { xs: 2, sm: 3 }, gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fit, minmax(200px, 1fr))' } }}>
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Power sx={{ fontSize: 20, color: '#009A75' }} />
                            <Typography variant="subtitle2">
                                {translate('resources.ocpp-templates.stats.chargers')}
                            </Typography>
                        </Box>
                        <Typography sx={{ fontWeight: 600, fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                            {chargers.length}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {translate('resources.ocpp-templates.chargersUsing')}
                        </Typography>
                    </Box>
                    
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Speed sx={{ fontSize: 20, color: '#FFA726' }} />
                            <Typography variant="subtitle2">
                                {translate('resources.ocpp-templates.stats.totalPower')}
                            </Typography>
                        </Box>
                        <Typography sx={{ fontWeight: 600, fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                            {totalPowerManaged.toFixed(0)} kW
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {translate('resources.ocpp-templates.stats.totalPowerDesc')}
                        </Typography>
                    </Box>
                    
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Timer sx={{ fontSize: 20, color: '#1976D2' }} />
                            <Typography variant="subtitle2">
                                {translate('resources.ocpp-templates.stats.normalInterval')}
                            </Typography>
                        </Box>
                        <Typography sx={{ fontWeight: 600, fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                            {record.normalIntervalSeconds}s
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {translate('resources.ocpp-templates.stats.normalIntervalDesc')}
                        </Typography>
                    </Box>
                </Box>
                
                {chargers.length > 0 && (
                    <Alert severity="warning" sx={{ mt: 2 }} icon={<Warning />}>
                        {translate('resources.ocpp-templates.inUseWarning')}
                    </Alert>
                )}
                
                {record.isDefault && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                        {translate('resources.ocpp-templates.defaultTemplateInfo')}
                    </Alert>
                )}
            </CardContent>
        </Card>
    );
};

export const OCPPTemplatesEdit = () => {
    const translate = useTranslate();
    const notify = useNotify();
    const redirect = useRedirect();
    const [tabValue, setTabValue] = useState(0);
    const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };

    const handleSave = (data: any) => {
        // Convert seconds (form field) to minutes (API field). Pass the raw
        // value through whenever it's defined — including 0 — so the backend
        // can reject it via Zod instead of us silently coercing falsy inputs
        // to `undefined` and stripping the field from the payload.
        const finalData = {
            ...data,
            approachingThresholdMinutes:
                data.approachingThresholdSeconds === undefined || data.approachingThresholdSeconds === null
                    ? data.approachingThresholdSeconds
                    : Number(data.approachingThresholdSeconds) / 60,
            nearThresholdMinutes:
                data.nearThresholdSeconds === undefined || data.nearThresholdSeconds === null
                    ? data.nearThresholdSeconds
                    : Number(data.nearThresholdSeconds) / 60,
        };

        // Remove the seconds fields as we send minutes to the API
        delete finalData.approachingThresholdSeconds;
        delete finalData.nearThresholdSeconds;

        return finalData;
    };

    return (
        <Edit
            title={false}
            component="div"
            actions={false}
            redirect="list"
            transform={handleSave}
            mutationMode="pessimistic"
            mutationOptions={{
                onSuccess: () => {
                    setServerErrors({});
                    notify('resources.ocpp-templates.notifications.updated', { type: 'success' });
                    redirect('/ocpp-templates');
                },
                onError: (error: any) => {
                    const apiError = error?.body || error;
                    const rawErrors = apiError?.errors as Record<string, string[] | string> | undefined;
                    const fieldErrors: Record<string, string> = {};
                    const summaryLines: string[] = [];
                    if (rawErrors) {
                        for (const [path, msgs] of Object.entries(rawErrors)) {
                            const rawMsg = Array.isArray(msgs) ? msgs[0] : String(msgs);
                            const translated = translateServerMessage(rawMsg, translate);
                            const field = mapOCPPTemplateBackendPath(path);
                            fieldErrors[field] = translated;
                            const label = translate(ocppTemplateFieldLabelKey(field), { _: field });
                            summaryLines.push(`• ${label}: ${translated}`);
                        }
                    }
                    setServerErrors(fieldErrors);

                    const headerKey = apiError?.errorCode
                        ? `errors.${apiError.errorCode}`
                        : 'validation.formHasErrors';
                    const header = translate(headerKey, { _: apiError?.message || 'Error' });

                    if (summaryLines.length > 0) {
                        notify(`${header}\n${summaryLines.join('\n')}`, {
                            type: 'error',
                            multiLine: true,
                            autoHideDuration: 10000,
                        });
                    } else {
                        notify(header, { type: 'error', autoHideDuration: 6000 });
                    }
                },
            }}
            sx={{
                backgroundColor: 'transparent',
                maxWidth: '100%',
                overflow: 'hidden',
                '& .RaEdit-main': {
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                },
            }}
        >
            <SimpleForm
                toolbar={<EditToolbar />}
                sx={{
                    maxWidth: '100%',
                    overflow: 'hidden',
                    '& .MuiPaper-root': {
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                    },
                    '& > .MuiCardContent-root, & > div > .MuiCardContent-root': {
                        px: { xs: 0, sm: 2 },
                        py: { xs: 1, sm: 2 },
                        maxWidth: '100%',
                        overflow: 'hidden',
                    },
                    padding: { xs: 0, sm: 'inherit' },
                }}
            >
                <ServerErrorBinder errors={serverErrors} />
                <Box sx={{ width: '100%', maxWidth: 800, margin: '0 auto', pb: { xs: 10, sm: 2 }, '& .MuiFormControl-root': { width: '100%' } }}>
                    <PageHeader />

                    {/* Usage Overview */}
                    <UsageOverviewSection />
                    
                    <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: { xs: 2, sm: 3 } }}>
                        <Tabs
                            value={tabValue}
                            onChange={handleTabChange}
                            variant={isMobile ? "fullWidth" : "scrollable"}
                            scrollButtons={isMobile ? false : "auto"}
                            allowScrollButtonsMobile
                            sx={{
                                minHeight: { xs: 40, sm: 48 },
                                '& .MuiTab-root': {
                                    minWidth: { xs: 0, sm: 90 },
                                    minHeight: { xs: 40, sm: 48 },
                                    px: { xs: 0.5, sm: 2 },
                                    py: { xs: 0.75, sm: 1.5 },
                                    fontSize: { xs: '0.65rem', sm: '0.875rem' },
                                    lineHeight: 1.1,
                                    whiteSpace: 'normal',
                                    wordBreak: 'break-word',
                                    textTransform: 'none',
                                },
                            }}
                        >
                            <Tab label={translate('resources.ocpp-templates.tabs.general')} />
                            <Tab label={translate('resources.ocpp-templates.tabs.intervals')} />
                            <Tab label={translate('resources.ocpp-templates.tabs.advanced')} />
                            <Tab label={translate('resources.ocpp-templates.tabs.danger')} />
                        </Tabs>
                    </Box>
                    
                    <TabPanel value={tabValue} index={0}>
                        {/* Basic Information */}
                        <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                            <CardContent sx={{ p: { xs: 1.5, sm: 3 } }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, sm: 3 } }}>
                                    <Info sx={{ mr: 2, color: '#009A75' }} />
                                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                        {translate('resources.ocpp-templates.sections.basic')}
                                    </Typography>
                                </Box>
                                
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <Box sx={{ '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                        <TextInput
                                            source="templateName"
                                            label={translate('resources.ocpp-templates.fields.templateName')}
                                            validate={[
                                                required(),
                                                maxLength(255, translate("resources.ocpp-templates.validation.nameTooLong")),
                                            ]}
                                            fullWidth
                                            helperText={translate('resources.ocpp-templates.helpers.templateName')}
                                            sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                                        />
                                    </Box>

                                    <Box sx={{ '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                        <TextInput
                                            source="description"
                                            label={translate('resources.ocpp-templates.fields.description')}
                                            multiline
                                            rows={3}
                                            validate={[
                                                maxLength(500, translate("resources.ocpp-templates.validation.descriptionTooLong")),
                                            ]}
                                            fullWidth
                                            helperText={translate('resources.ocpp-templates.helpers.description')}
                                        />
                                    </Box>
                                    
                                    <FormDataConsumer>
                                        {({ formData }) => (
                                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                                <Box>
                                                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                                        {translate('resources.ocpp-templates.fields.ocppVersion')}
                                                    </Typography>
                                                    <Chip 
                                                        label={translate(`resources.ocpp-templates.ocppVersions.${formData.ocppVersion || '1.6'}`)}
                                                        sx={{ 
                                                            backgroundColor: '#E3F2FD',
                                                            color: '#1976D2',
                                                            fontWeight: 600,
                                                        }}
                                                    />
                                                </Box>
                                                <Box>
                                                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                                        {translate('resources.ocpp-templates.fields.ocppProtocol')}
                                                    </Typography>
                                                    <Chip 
                                                        label={translate(`resources.ocpp-templates.ocppProtocols.${formData.ocppProtocol || 'json'}`)}
                                                        sx={{ 
                                                            backgroundColor: '#F3E5F5',
                                                            color: '#7B1FA2',
                                                            fontWeight: 600,
                                                        }}
                                                    />
                                                </Box>
                                            </Box>
                                        )}
                                    </FormDataConsumer>
                                    
                                    <BooleanInput
                                        source="isActive"
                                        label={translate('resources.ocpp-templates.fields.isActive')}
                                        sx={{ mb: 2 }}
                                    />
                                </Box>
                            </CardContent>
                        </Card>
                    </TabPanel>
                    
                    <TabPanel value={tabValue} index={1}>
                        {/* Interval Configuration */}
                        <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                            <CardContent sx={{ p: { xs: 1.5, sm: 3 } }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, sm: 3 } }}>
                                    <Timer sx={{ mr: 2, color: '#009A75' }} />
                                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                        {translate('resources.ocpp-templates.sections.intervals')}
                                    </Typography>
                                </Box>
                                
                                <Alert severity="info" sx={{ mb: { xs: 2, sm: 3 } }}>
                                    {translate('resources.ocpp-templates.intervalExplanation')}
                                </Alert>
                                
                                {/* Normal Interval */}
                                <Box sx={{ mb: { xs: 2, sm: 3 } }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                                        {translate('resources.ocpp-templates.normalInterval')}
                                    </Typography>
                                    <Box sx={{ '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                        <WheelSafeNumberInput
                                            source="normalIntervalSeconds"
                                            label={translate('resources.ocpp-templates.fields.normalIntervalSeconds')}
                                            validate={[
                                                required(),
                                                minValue(10, translate("resources.ocpp-templates.validation.intervalTooLow")),
                                                maxValue(300, translate("resources.ocpp-templates.validation.intervalTooHigh")),
                                            ]}
                                            fullWidth
                                            helperText={translate('resources.ocpp-templates.helpers.normalIntervalSeconds')}
                                            sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                                        />
                                    </Box>
                                </Box>
                                
                                <Divider sx={{ my: { xs: 2, sm: 3 } }} />
                                
                                {/* Initial Fast Interval */}
                                <Box sx={{ mb: { xs: 2, sm: 3 } }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                                        {translate('resources.ocpp-templates.initialFastInterval')}
                                    </Typography>
                                    <BooleanInput
                                        source="initialFastIntervalEnabled"
                                        label={translate('resources.ocpp-templates.fields.initialFastIntervalEnabled')}
                                        sx={{ mb: 2 }}
                                    />
                                    <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'flex-start' } }}>
                                        <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                            <WheelSafeNumberInput
                                                source="initialFastIntervalSeconds"
                                                label={translate('resources.ocpp-templates.fields.initialFastIntervalSeconds')}
                                                validate={[
                                                    minValue(1, translate("resources.ocpp-templates.validation.intervalTooLow")),
                                                    maxValue(30, translate("resources.ocpp-templates.validation.intervalTooHigh")),
                                                ]}
                                                fullWidth
                                                helperText={translate('resources.ocpp-templates.helpers.initialFastIntervalSeconds')}
                                                sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                                            />
                                        </Box>
                                        <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                            <WheelSafeNumberInput
                                                source="initialFastIntervalCount"
                                                label={translate('resources.ocpp-templates.fields.initialFastIntervalCount')}
                                                validate={[
                                                    minValue(1, translate("resources.ocpp-templates.validation.countTooLow")),
                                                    maxValue(50, translate("resources.ocpp-templates.validation.countTooHigh")),
                                                ]}
                                                fullWidth
                                                helperText={translate('resources.ocpp-templates.helpers.initialFastIntervalCount')}
                                                sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                                            />
                                        </Box>
                                    </Box>
                                </Box>
                                
                                <Divider sx={{ my: { xs: 2, sm: 3 } }} />
                                
                                {/* Approaching Interval */}
                                <Box sx={{ mb: { xs: 2, sm: 3 } }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                                        {translate('resources.ocpp-templates.approachingInterval')}
                                    </Typography>
                                    <BooleanInput
                                        source="approachingIntervalEnabled"
                                        label={translate('resources.ocpp-templates.fields.approachingIntervalSeconds')}
                                        sx={{ mb: 2 }}
                                    />
                                    <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'flex-start' } }}>
                                        <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                            <WheelSafeNumberInput
                                                source="approachingIntervalSeconds"
                                                label={translate('resources.ocpp-templates.fields.approachingIntervalSeconds')}
                                                validate={[
                                                    minValue(1, translate("resources.ocpp-templates.validation.intervalTooLow")),
                                                    maxValue(30, translate("resources.ocpp-templates.validation.intervalTooHigh")),
                                                ]}
                                                fullWidth
                                                helperText={translate('resources.ocpp-templates.helpers.approachingIntervalSeconds')}
                                                sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                                            />
                                        </Box>
                                        <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                            <WheelSafeNumberInput
                                                source="approachingThresholdSeconds"
                                                label={translate('resources.ocpp-templates.fields.approachingThresholdSeconds')}
                                                validate={[
                                                    minValue(60, translate("resources.ocpp-templates.validation.thresholdTooLow")),
                                                    maxValue(1800, translate("resources.ocpp-templates.validation.thresholdTooHigh")),
                                                ]}
                                                fullWidth
                                                helperText={translate('resources.ocpp-templates.helpers.approachingThresholdMinutes')}
                                                sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                                            />
                                        </Box>
                                    </Box>
                                </Box>
                                
                                <Divider sx={{ my: { xs: 2, sm: 3 } }} />
                                
                                {/* Near Interval */}
                                <Box>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                                        {translate('resources.ocpp-templates.nearInterval')}
                                    </Typography>
                                    <BooleanInput
                                        source="nearIntervalEnabled"
                                        label={translate('resources.ocpp-templates.fields.nearIntervalSeconds')}
                                        sx={{ mb: 2 }}
                                    />
                                    <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'flex-start' } }}>
                                        <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                            <WheelSafeNumberInput
                                                source="nearIntervalSeconds"
                                                label={translate('resources.ocpp-templates.fields.nearIntervalSeconds')}
                                                validate={[
                                                    minValue(1, translate("resources.ocpp-templates.validation.intervalTooLow")),
                                                    maxValue(10, translate("resources.ocpp-templates.validation.intervalTooHigh")),
                                                ]}
                                                fullWidth
                                                helperText={translate('resources.ocpp-templates.helpers.nearIntervalSeconds')}
                                                sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                                            />
                                        </Box>
                                        <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                            <WheelSafeNumberInput
                                                source="nearThresholdSeconds"
                                                label={translate('resources.ocpp-templates.fields.nearThresholdSeconds')}
                                                validate={[
                                                    minValue(30, translate("resources.ocpp-templates.validation.thresholdTooLow")),
                                                    maxValue(600, translate("resources.ocpp-templates.validation.thresholdTooHigh")),
                                                ]}
                                                fullWidth
                                                helperText={translate('resources.ocpp-templates.helpers.nearThresholdMinutes')}
                                                sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                                            />
                                        </Box>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </TabPanel>
                    
                    <TabPanel value={tabValue} index={2}>
                        {/* Advanced Configuration */}
                        <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                            <CardContent sx={{ p: { xs: 1.5, sm: 3 } }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, sm: 3 } }}>
                                    <TrendingUp sx={{ mr: 2, color: '#009A75' }} />
                                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                        {translate('resources.ocpp-templates.sections.advanced')}
                                    </Typography>
                                </Box>
                                
                                {/* Hysteresis */}
                                <Box sx={{ mb: { xs: 2, sm: 3 } }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                                        {translate('resources.ocpp-templates.hysteresis')}
                                    </Typography>
                                    <Alert severity="info" sx={{ mb: 2 }}>
                                        {translate('resources.ocpp-templates.hysteresisExplanation')}
                                    </Alert>
                                    <BooleanInput
                                        source="hysteresisEnabled"
                                        label={translate('resources.ocpp-templates.hysteresis')}
                                        sx={{ mb: 2 }}
                                    />
                                    <Box sx={{ '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                        <WheelSafeNumberInput
                                            source="hysteresisMinutes"
                                            label={translate('resources.ocpp-templates.hysteresis')}
                                            validate={[
                                                minValue(0.5, translate("resources.ocpp-templates.validation.hysteresisTooLow")),
                                                maxValue(10, translate("resources.ocpp-templates.validation.hysteresisTooHigh")),
                                            ]}
                                            fullWidth
                                            helperText={translate('resources.ocpp-templates.helpers.hysteresisMinutes')}
                                            sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                                        />
                                    </Box>
                                </Box>
                                
                                <Divider sx={{ my: { xs: 2, sm: 3 } }} />
                                
                                {/* Power Smoothing */}
                                <Box>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                                        {translate('resources.ocpp-templates.powerSmoothing')}
                                    </Typography>
                                    <Alert severity="info" sx={{ mb: 2 }}>
                                        {translate('resources.ocpp-templates.powerSmoothingExplanation')}
                                    </Alert>
                                    <BooleanInput
                                        source="powerSmoothingEnabled"
                                        label={translate('resources.ocpp-templates.fields.powerSmoothingEnabled')}
                                        sx={{ mb: 2 }}
                                    />
                                    <Box sx={{ '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                        <WheelSafeNumberInput
                                            source="powerSmoothingFactor"
                                            label={translate('resources.ocpp-templates.powerSmoothing')}
                                            validate={[
                                                minValue(0, translate("resources.ocpp-templates.validation.smoothingTooLow")),
                                                maxValue(1, translate("resources.ocpp-templates.validation.smoothingTooHigh")),
                                            ]}
                                            step={0.1}
                                            fullWidth
                                            helperText={translate('resources.ocpp-templates.helpers.powerSmoothingFactor')}
                                            sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                                        />
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </TabPanel>
                    
                    <TabPanel value={tabValue} index={3}>
                        <Card sx={{ overflow: 'hidden' }}>
                            <CardContent sx={{ p: { xs: 1.5, sm: 3 } }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, sm: 3 } }}>
                                    <Delete sx={{ mr: 2, color: '#F44336' }} />
                                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                        {translate('resources.ocpp-templates.sections.dangerZone')}
                                    </Typography>
                                </Box>
                                
                                <DeleteTemplateSection />
                            </CardContent>
                        </Card>
                    </TabPanel>
                </Box>
            </SimpleForm>
        </Edit>
    );
};
